/**
 * A picture of the cloth on each colour button.
 *
 * A flat pill, from the colour word, with the garment's own photograph kept as
 * the fallback for the values that are not colours at all — "Flower", "Ombre",
 * "London". A pill cannot show a herringbone, which is the argument against it;
 * it is also what this shop's customers have always been shown and what reads
 * as a swatch anywhere else, which is the argument for.
 *
 * A theme asset rather than an app embed, because this should simply be how the
 * shop looks rather than a switch somebody has to find and remember. Which
 * means it cannot be handed its data by Liquid, so it asks Shopify: the .js
 * endpoint every storefront has carries the variants and their images, and it
 * is the same JSON the theme's own scripts already fetch. No template to edit,
 * no block to place, one line in theme.liquid and it is on everywhere.
 *
 * Nothing new is drawn. The thumbnail goes inside the theme's own label, so the
 * radio underneath stays the control: which one is checked, what a sold-out
 * value looks like, keyboard focus and the form submission are all still the
 * theme's, and none of it had to be reimplemented to hang a picture off it.
 */
(function () {
  'use strict';

  var SIZE = 26;

  var match = location.pathname.match(/\/products\/([^/?#]+)/);
  if (!match) return;

  var key = function (v) { return String(v).trim().toLowerCase(); };

  /**
   * The shop's colour words, in hex.
   *
   * Built from the catalogue rather than from a colour library: sixty-four
   * distinct values across a thousand products, and the ones that matter most
   * are the ones CSS has never heard of. Charcoal, camel, chestnut, taupe,
   * burgundy, champagne, sage, oak and timber are menswear words, not web
   * colours, and asking the browser to parse them returns nothing at all.
   *
   * The compounds are listed outright instead of being derived. "Rose gold" is
   * not rose mixed with gold, and "off white" is not a shade of off.
   */
  var PALETTE = {
    black: '#1A1A1A', navy: '#1B2A4A', brown: '#6B4A2F', grey: '#8C8C8C',
    gray: '#8C8C8C', green: '#2F6B3A', blue: '#2E5FA3', tan: '#C39A6B',
    white: '#FFFFFF', pink: '#E8A5B8', charcoal: '#40444A', olive: '#6B6B3A',
    beige: '#D8C7A8', burgundy: '#6E1F2E', silver: '#C4C7CA', camel: '#C19A6B',
    cream: '#F2E8D5', red: '#B32027', gold: '#C9A227', chestnut: '#7B3F2A',
    purple: '#6A3D8F', yellow: '#E8C93A', orange: '#D9702A', taupe: '#A89684',
    ivory: '#F5F0E1', bronze: '#A97142', sage: '#9CAF88', oak: '#B08D57',
    wine: '#5C1A2B', champagne: '#F0DFC0', violet: '#7A52A1', salmon: '#E9967A',
    aqua: '#4FC3C7', azure: '#2E7BC4', lilac: '#C3A8D8', fuchsia: '#C2317E',
    maroon: '#5B1A26', teal: '#21707A', sand: '#DCC9A6', ginger: '#B5651D',
    timber: '#8B6A45', rose: '#C98A93',

    'sky blue': '#87BEDE', 'light blue': '#A9CCE3', 'dark green': '#1E4630',
    'light green': '#A4C89A', 'rose gold': '#C8938A', 'off white': '#F7F4EC',
    'rose pink': '#E5A3AE', 'light pink': '#F3C6D2', 'dusty pink': '#D3A0A6',
    'baby pink': '#F6D2DC', 'light grey': '#C9CBCD', 'light brown': '#9C6B45',
    'aqua blue': '#5BB8D4', 'dark grey': '#5A5E63', 'dark blue': '#16304F',
  };

  /* Two colours in one name — "black gold", "brown beige" — are two colours,
     and a pill split down the middle says so where picking one of them lies. */
  function paint(value) {
    var at = key(value).replace(/\s+/g, ' ');
    if (PALETTE[at]) return { fill: PALETTE[at] };

    var words = at.split(' ');
    var found = [];
    for (var i = 0; i < words.length; i++) {
      var hex = PALETTE[words[i]];
      if (hex && found.indexOf(hex) === -1) found.push(hex);
    }
    if (found.length > 1) {
      return { fill: 'linear-gradient(135deg, ' + found[0] + ' 0 50%, ' + found[1] + ' 50% 100%)' };
    }
    if (found.length === 1) return { fill: found[0] };

    /* "Flower", "Ombre", "London" — a pattern or a name, not a colour. The
       photograph is a better answer than a blank circle. */
    return null;
  }

  function labelValue(label) {
    var span = label.querySelector('.js-value');
    if (span && span.textContent) return span.textContent.trim();
    /* No .js-value means a theme that writes the text straight into the label;
       the paired input still knows the answer. */
    var input = label.htmlFor && document.getElementById(label.htmlFor);
    return input ? String(input.value || '').trim() : label.textContent.trim();
  }

  function build(product) {
    var options = product.options || [];
    var index = -1;
    for (var i = 0; i < options.length; i++) {
      var name = key(typeof options[i] === 'string' ? options[i] : options[i].name);
      if (name.indexOf('colour') > -1 || name.indexOf('color') > -1) { index = i; break; }
    }
    if (index < 0) return null;

    var byValue = {};
    (product.variants || []).forEach(function (v) {
      var value = v.options && v.options[index];
      if (!value) return;
      var at = key(value);
      if (byValue[at]) return;
      var image = v.featured_image && v.featured_image.src;
      if (image) byValue[at] = image;
    });
    return Object.keys(byValue).length ? byValue : null;
  }

  /**
   * One width for every button in the row.
   *
   * "Brown" is wider than "Pink", so the buttons came out different lengths and
   * the row read as ragged rather than as a set of equal choices. Measured
   * rather than guessed at, because the widest word varies by product and a
   * fixed width would either clip "Champagne" or strand "Red" in whitespace.
   *
   * Written as a minimum, so a value nobody anticipated still gets the room it
   * needs instead of being cut off.
   */
  function equalise(fieldset) {
    var labels = fieldset.querySelectorAll('.tc-cs__label');
    if (!labels.length) return;

    var widest = 0;
    var i;
    for (i = 0; i < labels.length; i++) {
      labels[i].style.minWidth = '';
      widest = Math.max(widest, labels[i].getBoundingClientRect().width);
    }
    for (i = 0; i < labels.length; i++) {
      labels[i].style.minWidth = Math.ceil(widest) + 'px';
    }
  }

  function dress(byValue) {
    var touched = false;
    var fieldsets = document.querySelectorAll('[data-option]');
    for (var f = 0; f < fieldsets.length; f++) {
      var fieldset = fieldsets[f];
      var name = key(fieldset.getAttribute('data-option') || '');
      if (name.indexOf('colour') === -1 && name.indexOf('color') === -1) continue;

      var labels = fieldset.querySelectorAll('label');
      for (var i = 0; i < labels.length; i++) {
        var label = labels[i];
        if (label.querySelector('.tc-cs__img')) continue; // already dressed

        var value = labelValue(label);
        var colour = paint(value);
        var url = byValue[key(value)];
        if (!colour && !url) continue;

        var mark;
        if (colour) {
          mark = document.createElement('span');
          mark.style.background = colour.fill;
        } else {
          /* Shopify serves any width off the same file; asking for the size we
             draw rather than the full product shot keeps this to a few KB. */
          mark = document.createElement('img');
          mark.src = String(url).replace(/(\.[a-z]+)(\?|$)/i, '_120x$1$2');
          mark.alt = '';
          mark.loading = 'lazy';
          mark.width = SIZE;
          mark.height = SIZE;
        }
        mark.className = 'tc-cs__img';

        label.classList.add('tc-cs__label');
        label.insertBefore(mark, label.firstChild);
        touched = true;
      }

      /* Only when something was actually added. Writing widths on every pass
         would be a style change the observer below sees as a mutation, which
         schedules another pass, which writes the widths again. */
      if (touched) equalise(fieldset);
    }
    return touched;
  }

  fetch('/products/' + match[1] + '.js', { credentials: 'same-origin' })
    .then(function (r) { return r.ok ? r.json() : null; })
    .then(function (product) {
      if (!product) return;
      var byValue = build(product);
      if (!byValue) return;

      dress(byValue);

      /* Themes re-render the option block on a variant change, and quick-buy
         draws a second one in a drawer. Watching for that is cheaper than
         hunting for every event a theme might or might not fire. */
      if (window.MutationObserver) {
        var pending = null;
        new MutationObserver(function () {
          if (pending) return;
          pending = setTimeout(function () { pending = null; dress(byValue); }, 50);
        }).observe(document.body, { childList: true, subtree: true });
      }
    })
    .catch(function () {
      /* A swatch is not worth an error on somebody's product page. */
    });
})();
